import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, ClockIcon, MapPinIcon, TagIcon } from '@heroicons/react/24/solid';
import { formatLocation } from '../../../utils/formatters';

const TourCard = ({ tour }) => {
  const [imageError, setImageError] = useState(false);
  
  // Get the API base URL for images
  const apiBaseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
  
  // Format price with commas
  const formatPrice = (price) => {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  
  // Fallback images for different categories
  const getFallbackImage = (category) => {
    const fallbacks = {
      'Wildlife Safari': 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Beach Vacation': 'https://images.unsplash.com/photo-1520454974749-611b7248ffdb?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Mountain Climbing': 'https://images.unsplash.com/photo-1486870591958-9b9d0d1dda99?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Adventure': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'Cultural Experience': 'https://images.unsplash.com/photo-1489493585363-d69421e0edd3?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'City Tour': 'https://images.unsplash.com/photo-1611348524140-53c9a25ad10a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80',
      'default': 'https://images.unsplash.com/photo-1528733918455-5a59687cedf0?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=600&q=80'
    };
    
    if (!category) return fallbacks.default;
    
    // If we have categories as an array, use the first one
    const categoryName = Array.isArray(category) && category.length > 0 
      ? category[0].name 
      : (typeof category === 'string' ? category : 'default');
      
    return fallbacks[categoryName] || fallbacks.default;
  };
  
  // Get image URL
  const getImageUrl = () => {
    if (imageError) return getFallbackImage(tour.categories);
    
    // If featured_image is available
    if (tour.featured_image) {
      // If image is a full URL
      if (tour.featured_image.startsWith('http://') || tour.featured_image.startsWith('https://')) {
        return tour.featured_image;
      }
      
      // If image is a relative path - properly handle storage paths
      return `${apiBaseUrl}/storage/${tour.featured_image}`;
    }
    
    // If images array is available, use the first one
    if (tour.images && Array.isArray(tour.images) && tour.images.length > 0) {
      const firstImage = tour.images[0];
      if (firstImage.startsWith('http://') || firstImage.startsWith('https://')) {
        return firstImage;
      }
      return `${apiBaseUrl}${firstImage.startsWith('/') ? '' : '/'}${firstImage}`;
    }
    
    return getFallbackImage(tour.categories);
  };
  
  // Get category name
  const getCategoryName = () => {
    if (Array.isArray(tour.categories) && tour.categories.length > 0) {
      return tour.categories[0].name;
    }
    return tour.category || 'Tour';
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <Link to={`/tours/${tour.id}`} className="block">
        <div className="relative">
          <img 
            src={getImageUrl()} 
            alt={tour.title} 
            className="w-full h-48 object-cover"
            onError={() => setImageError(true)}
          />
          <div className="absolute top-4 right-4 bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold">
            ${formatPrice(tour.price)}
          </div>
          <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 px-3 py-1 rounded-full text-sm font-semibold flex items-center">
            <TagIcon className="h-4 w-4 mr-1 text-primary" />
            {getCategoryName()}
          </div>
        </div>
      </Link>

      <div className="p-5">
        <Link to={`/tours/${tour.id}`} className="block">
          <h3 className="text-xl font-bold mb-2 text-gray-800 hover:text-primary transition-colors duration-300">
            {tour.title}
          </h3>
        </Link>

        <div className="flex items-center mb-3">
          <div className="flex items-center text-yellow-500 mr-2">
            <StarIcon className="h-5 w-5" />
            <span className="ml-1 font-medium">{tour.rating || 4.5}</span>
          </div>
          <span className="text-gray-500 text-sm">({tour.reviews_count || tour.reviewCount || 0} reviews)</span>
        </div>

        <p className="text-gray-600 mb-4 line-clamp-2">
          {tour.description}
        </p>

        <div className="flex flex-wrap items-center text-gray-600 mb-4">
          <div className="flex items-center mr-4 mb-2">
            <ClockIcon className="h-4 w-4 mr-1 text-primary" />
            <span>{tour.duration} {tour.duration === 1 ? 'day' : 'days'}</span>
          </div>
          <div className="flex items-center mb-2">
            <MapPinIcon className="h-4 w-4 mr-1 text-primary" />
            <span>{formatLocation(tour.location)}</span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Link 
            to={`/tours/${tour.id}`} 
            className="text-primary font-semibold hover:underline"
          >
            View Details
          </Link>
          <Link 
            to={`/booking/${tour.id}`} 
            className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors duration-300"
          >
            Book Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TourCard;
